import React, { ReactElement } from "react";
import { ComponentGroup } from "../componentGroup/componentGroup";
import { type IContent } from "@kunduz/data-access-cms-api";
import {
  BenefitGroup,
  DynamicHero,
  Faq,
  Feature,
  FeatureGroup,
  FeatureWithImage,
  Legal,
  LogoGroup,
  Team,
  Testimonials,
  VideoTestimonials,
} from "@kunduz/ui-components";
import {
  GetStaticComponent,
  TrFaqCommunications,
  getCmsIcon,
  PricingWithContext,
  DynamicContactUs,
  FabButtonWithContext,
  DetailedAccordionWithContext,
  RedirectionWithContext,
  UserReviewsWithContext,
  ScrollingBannerWithContext,
  QuickNavigationWithContext,
  UserReviewCtaWithContext,
  TableWithContext,
  FeatureV2WithContext,
  PlanDetailV2,
  ImageBannerWithContext,
  ModalComponent,
  DividerComponent,
  SummaryFeatureGroupComponent,
  MediaComponent,
  SectionHeaderComponent,
  ButtonGroup,
} from "../index";

export const ComponentRenderer = (props: Props): ReactElement => {
  if (!props.components) return <></>;

  return props.components.map((c, i) => {
    switch (c.component_type) {
      case "faq":
        return (
          <Faq
            key={c.name}
            title={(c.data[0] as IContent.FaqComponentData).title}
            description={(c.data[0] as IContent.FaqComponentData).desc}
            faqItems={(c.data[0] as IContent.FaqComponentData).items.map(
              (item, i) => ({
                title: item.title,
                content: item.content,
              }),
            )}
            communicationsComponent={
              (c.data[0] as IContent.FaqComponentData).is_comms_enabled ? (
                <TrFaqCommunications />
              ) : undefined
            }
          />
        );
      case "testimonials":
        return (
          <Testimonials
            key={c.name}
            items={(c.data as IContent.TestimonialComponentData[]).map(
              (item) => ({
                image: item.image,
                rating: 5,
                text: item.content,
                username: item.full_name,
                userlabel: item.job_title,
                usericon: item.icon,
              }),
            )}
          />
        );
      case "feature":
        return (
          <Feature
            key={c.name}
            name={c.name}
            title={(c.data as IContent.FeatureComponentData[])[0].title}
            supportingText={
              (c.data as IContent.FeatureComponentData[])[0].content
            }
            variant={(c.data as IContent.FeatureComponentData[])[0].bg_color}
            imagePosition={
              (c.data as IContent.FeatureComponentData[])[0].image_position
            }
            imageUrl={(c.data as IContent.FeatureComponentData[])[0].image}
            badgeText={
              (c.data as IContent.FeatureComponentData[])[0].badge_title
            }
            badgeIcon={getCmsIcon(
              (c.data as IContent.FeatureComponentData[])[0].badge_icon,
            )}
            featureItems={(
              c.data as IContent.FeatureComponentData[]
            )[0].items.map((item, i) => ({
              label: item.content,
              title: item.title,
              icon: getCmsIcon(item.icon),
            }))}
            primaryActionTitle={
              (c.data as IContent.FeatureComponentData[])[0].primary_cta_title
            }
            primaryActionUrl={
              (c.data as IContent.FeatureComponentData[])[0].primary_cta_link
            }
            primaryActionIcon={getCmsIcon(
              (c.data as IContent.FeatureComponentData[])[0].primary_cta_icon,
            )}
            secondaryActionTitle={
              (c.data as IContent.FeatureComponentData[])[0].secondary_cta_title
            }
            secondaryActionUrl={
              (c.data as IContent.FeatureComponentData[])[0].secondary_cta_link
            }
            secondaryActionIcon={getCmsIcon(
              (c.data as IContent.FeatureComponentData[])[0].secondary_cta_icon,
            )}
            secondaryImageUrl={
              (c.data as IContent.FeatureComponentData[])[0].bottom_image
            }
            id={(c.data as IContent.FeatureComponentData[])[0].anchor_tag}
          />
        );
      case "package":
        return (
          <PricingWithContext
            key={c.name}
            title={(c.data as IContent.PricingComponentData[])[0].title}
            badgeText={(c.data as IContent.PricingComponentData[])[0].badge}
            badgeIcon={getCmsIcon(
              (c.data as IContent.PricingComponentData[])[0].badge_icon,
            )}
            desc={(c.data as IContent.PricingComponentData[])[0].highlight_text}
            gradeGroups={
              (c.data as IContent.PricingComponentData[])[0].grade_groups
            }
            periodGroups={
              (c.data as IContent.PricingComponentData[])[0].period_groups
            }
            plans={(c.data as IContent.PricingComponentData[])[0].prices}
            priceSwitchText={
              (c.data as IContent.PricingComponentData[])[0].price_switch_text
            }
            benefitTitle={
              props.lang == "tr" ? "PAKET İÇERİĞİ" : "PLAN CONTENTS"
            }
            enableGradeGroups={
              (c.data as IContent.PricingComponentData[])[0].enable_grade_group
            }
            enablePeriodGroups={
              (c.data as IContent.PricingComponentData[])[0].enable_period_group
            }
            lang={props.lang}
          />
        );
      case "benefit":
        return (
          <BenefitGroup
            key={c.name}
            title={(c.data as IContent.BenefitGroupComponentData[])[0].title}
            badgeText={
              (c.data as IContent.BenefitGroupComponentData[])[0].badge
            }
            desc={
              (c.data as IContent.BenefitGroupComponentData[])[0].highlight_text
            }
            theme={(c.data as IContent.BenefitGroupComponentData[])[0].color}
            badgeIcon={getCmsIcon(
              (c.data as IContent.BenefitGroupComponentData[])[0].badge_icon,
            )}
            benefits={(
              c.data as IContent.BenefitGroupComponentData[]
            )[0].items?.map((b) => ({
              imageUrl: b.image,
              title: b.title,
              description: b.content,
              bullets: b.bullets.map((x) => x.content),
              anchor_tag: b.anchor_tag,
            }))}
          />
        );
      case "team-component":
        return (
          <Team
            key={c.name}
            title={(c.data as IContent.TeamComponentData[])[0].title}
            supportingText={
              (c.data as IContent.TeamComponentData[])[0].highlight_text
            }
            members={(c.data as IContent.TeamComponentData[])[0].items.map(
              (m) => ({
                imageUrl: m.profile_pic,
                name: m.full_name,
                position: m.job_title,
                github: m.github,
                linkedin: m.linkedin,
                twitter: m.twitter,
              }),
            )}
          />
        );
      case "feature-group":
        return (
          <FeatureGroup
            key={c.name}
            title={(c.data as IContent.FeatureGroupComponentData[])[0].title}
            supportingText={
              (c.data as IContent.FeatureGroupComponentData[])[0].highlight_text
            }
            badgeText={
              (c.data as IContent.FeatureGroupComponentData[])[0].badge
            }
            badgeIcon={getCmsIcon(
              (c.data as IContent.FeatureGroupComponentData[])[0].badge_icon,
            )}
            features={(
              c.data as IContent.FeatureGroupComponentData[]
            )[0].items?.map((x) => ({
              desc: x.content,
              title: x.title,
              icon: getCmsIcon(x.icon),
              detailLabel: x.link_text,
              link: x.external_link,
              newTab: x.is_external_link_new_page,
            }))}
          />
        );
      case "text-content":
        return (
          <Legal
            key={c.name}
            title={(c.data as IContent.LegalComponentData[])[0].title}
            badgeText={(c.data as IContent.LegalComponentData[])[0].badge}
            content={(c.data as IContent.LegalComponentData[])[0].content}
            description={
              (c.data as IContent.LegalComponentData[])[0].highlight_text
            }
          />
        );
      case "video-testimonial":
        return (
          <VideoTestimonials
            badgeIcon={getCmsIcon(
              (c.data as IContent.VideoTestimonialComponentData[])[0]
                .badge_icon,
            )}
            badgeText={
              (c.data as IContent.VideoTestimonialComponentData[])[0].badge
            }
            title={
              (c.data as IContent.VideoTestimonialComponentData[])[0].title
            }
            supportingText={
              (c.data as IContent.VideoTestimonialComponentData[])[0].content
            }
            primaryActionIcon={getCmsIcon(
              (c.data as IContent.VideoTestimonialComponentData[])[0]
                .primary_cta_icon,
            )}
            primaryActionTitle={
              (c.data as IContent.VideoTestimonialComponentData[])[0]
                .primary_cta_title
            }
            primaryActionUrl={
              (c.data as IContent.VideoTestimonialComponentData[])[0]
                .primary_cta_link
            }
            secondaryActionIcon={getCmsIcon(
              (c.data as IContent.VideoTestimonialComponentData[])[0]
                .secondary_cta_icon,
            )}
            secondaryActionTitle={
              (c.data as IContent.VideoTestimonialComponentData[])[0]
                .secondary_cta_title
            }
            secondaryActionUrl={
              (c.data as IContent.VideoTestimonialComponentData[])[0]
                .secondary_cta_link
            }
            items={(
              c.data as IContent.VideoTestimonialComponentData[]
            )[0].items?.map((item) => ({
              title: item.title,
              thumbnail: item.thumbnail,
              videoUrl: item.file,
              desc: `${item.full_name} • ${item.degree}\n${item.university}`,
            }))}
          />
        );
      case "hero-component":
        return (
          <DynamicHero
            key={c.name}
            image={(c.data as IContent.HeroComponentData[])[0].image}
            title={(c.data as IContent.HeroComponentData[])[0].title}
            description={
              (c.data as IContent.HeroComponentData[])[0].description
            }
            rankingItems={(
              c.data as IContent.HeroComponentData[]
            )[0].ranking_items?.map((x) => x.text)}
            badgeText={(c.data as IContent.HeroComponentData[])[0].badge_text}
            badgeIcon={(c.data as IContent.HeroComponentData[])[0].badge_icon}
            primary_cta_icon={
              (c.data as IContent.HeroComponentData[])[0].primary_cta_icon
            }
            primary_cta_label={
              (c.data as IContent.HeroComponentData[])[0].primary_cta_label
            }
            primary_cta_url={
              (c.data as IContent.HeroComponentData[])[0].primary_cta_url
            }
            primary_cta_type={
              (c.data as IContent.HeroComponentData[])[0].primary_cta_type
            }
            primary_cta_target={
              (c.data as IContent.HeroComponentData[])[0].primary_cta_target
            }
            secondary_cta_icon={
              (c.data as IContent.HeroComponentData[])[0].secondary_cta_icon
            }
            secondary_cta_label={
              (c.data as IContent.HeroComponentData[])[0].secondary_cta_label
            }
            secondary_cta_url={
              (c.data as IContent.HeroComponentData[])[0].secondary_cta_url
            }
            secondary_cta_type={
              (c.data as IContent.HeroComponentData[])[0].secondary_cta_type
            }
            secondary_cta_target={
              (c.data as IContent.HeroComponentData[])[0].secondary_cta_target
            }
            gradientText={
              (c.data as IContent.HeroComponentData[])[0].meta
                ?.__gradient_text__
            }
            animatedText={
              (c.data as IContent.HeroComponentData[])[0].meta
                ?.__animated_text__
            }
            rating_star={
              (c.data as IContent.HeroComponentData[])[0].rating_star
            }
            rating_text={
              (c.data as IContent.HeroComponentData[])[0].rating_text
            }
            profile={(c.data as IContent.HeroComponentData[])[0].profile}
            buttonItems={
              (c.data as IContent.HeroComponentData[])[0].button_items
            }
            buttonItemsTitle={
              (c.data as IContent.HeroComponentData[])[0].button_items_title
            }
          />
        );
      case "contact-us-component":
        return (
          <DynamicContactUs
            key={c.name}
            title={(c.data as IContent.ContactUsComponentData[])[0].title}
            description={
              (c.data as IContent.ContactUsComponentData[])[0].description
            }
            image={(c.data as IContent.ContactUsComponentData[])[0].image}
            badge_text={
              (c.data as IContent.ContactUsComponentData[])[0].badge_text
            }
            badge_icon={getCmsIcon(
              (c.data as IContent.ContactUsComponentData[])[0].badge_icon,
            )}
            items={(c.data as IContent.ContactUsComponentData[])[0].items}
          />
        );
      case "collaboration-component":
        return (
          <LogoGroup
            key={c.name}
            name={c.name}
            badgeIcon={getCmsIcon(
              (c.data as IContent.LogoGroupComponentData[])[0].badge_icon,
            )}
            badgeText={
              (c.data as IContent.LogoGroupComponentData[])[0].badge_text
            }
            logoUrls={(
              c.data as IContent.LogoGroupComponentData[]
            )[0].items?.map((x) => x.image)}
            title={(c.data as IContent.LogoGroupComponentData[])[0].title}
            supportingText={
              (c.data as IContent.LogoGroupComponentData[])[0].description
            }
            primary_cta_icon={
              (c.data as IContent.LogoGroupComponentData[])[0]
                .primary_cta_icon &&
              getCmsIcon(
                (c.data as IContent.LogoGroupComponentData[])[0]
                  .primary_cta_icon,
              )
            }
            primary_cta_label={
              (c.data as IContent.LogoGroupComponentData[])[0].primary_cta_label
            }
            primary_cta_url={
              (c.data as IContent.LogoGroupComponentData[])[0].primary_cta_url
            }
            primary_cta_type={
              (c.data as IContent.LogoGroupComponentData[])[0].primary_cta_type
            }
            primary_cta_target={
              (c.data as IContent.LogoGroupComponentData[])[0]
                .primary_cta_target
            }
            secondary_cta_icon={
              (c.data as IContent.LogoGroupComponentData[])[0]
                .secondary_cta_icon &&
              getCmsIcon(
                (c.data as IContent.LogoGroupComponentData[])[0]
                  .secondary_cta_icon,
              )
            }
            secondary_cta_label={
              (c.data as IContent.LogoGroupComponentData[])[0]
                .secondary_cta_label
            }
            secondary_cta_url={
              (c.data as IContent.LogoGroupComponentData[])[0].secondary_cta_url
            }
            secondary_cta_type={
              (c.data as IContent.LogoGroupComponentData[])[0]
                .secondary_cta_type
            }
            secondary_cta_target={
              (c.data as IContent.LogoGroupComponentData[])[0]
                .secondary_cta_target
            }
          />
        );
      case "package-detail-v2":
        return (
          <PlanDetailV2
            key={c.name}
            data={(c.data as IContent.PlanDetailV2ComponentData[])[0]}
          />
        );
      case "floating-action-button-component":
        return (
          <FabButtonWithContext
            key={c.name}
            href={(c.data as IContent.FabButtonComponentData[])[0].url}
            target={(c.data as IContent.FabButtonComponentData[])[0].target}
            image={(c.data as IContent.FabButtonComponentData[])[0].image}
            name={c.name}
          />
        );
      case "redirection-component":
        return (
          <RedirectionWithContext
            key={c.name}
            title={(c.data as IContent.RedirectionComponentData[])[0].title}
            description={
              (c.data as IContent.RedirectionComponentData[])[0].description
            }
            buttonItemsTitle={
              (c.data as IContent.RedirectionComponentData[])[0]
                .button_items_title
            }
            buttonItems={
              (c.data as IContent.RedirectionComponentData[])[0].button_items
            }
            image={(c.data as IContent.RedirectionComponentData[])[0].image}
            rankingItems={
              (c.data as IContent.RedirectionComponentData[])[0].ranking_items
            }
          />
        );
      case "detailed-accordion-component":
        return (
          <DetailedAccordionWithContext
            key={c.name}
            data={(c.data as IContent.DetailedAccordionData[])[0]}
          />
        );
      case "user-review-component":
        return (
          <UserReviewsWithContext
            key={c.name}
            data={(c.data as IContent.UserReviewComponentData[])[0]}
          />
        );
      case "sliding-banner-component":
        return (
          <ScrollingBannerWithContext
            key={c.name}
            data={(c.data as IContent.SlidingBannerResponse[])[0]}
          />
        );
      case "quick-navigation-component":
        return (
          <QuickNavigationWithContext
            key={c.name}
            data={(c.data as IContent.QuickNavigationComponentData[])[0]}
          />
        );
      case "user-review-banner-component":
        return (
          <UserReviewCtaWithContext
            key={c.name}
            data={(c.data as IContent.UserReviewCtaComponentData[])[0]}
          />
        );
      case "table-component":
        return (
          <TableWithContext
            key={c.name}
            data={(c.data as IContent.TableComponentData[])[0]}
          />
        );
      case "image-banner-component":
        return (
          <ImageBannerWithContext
            key={c.name}
            data={(c.data as IContent.ImageBannerComponentData[])[0]}
          />
        );
      case "feature-image-component":
        return (
          <FeatureWithImage
            key={c.name}
            title={
              (c.data as IContent.FeatureWithImageComponentData[])[0].title
            }
            desc={
              (c.data as IContent.FeatureWithImageComponentData[])[0]
                .description
            }
            badgeIcon={getCmsIcon(
              (c.data as IContent.FeatureWithImageComponentData[])[0]
                .badge_icon,
            )}
            badgeText={
              (c.data as IContent.FeatureWithImageComponentData[])[0].badge
            }
            imagePosition={
              (c.data as IContent.FeatureWithImageComponentData[])[0]
                .image_position
            }
            imageUrl={
              (c.data as IContent.FeatureWithImageComponentData[])[0].image
            }
            bgImage={
              (c.data as IContent.FeatureWithImageComponentData[])[0].bg_image
            }
            size="md"
            anchorTag={
              (c.data as IContent.FeatureWithImageComponentData[])[0].anchor_tag
            }
          />
        );
      case "feature-v2-component":
        return (
          <FeatureV2WithContext
            key={c.name}
            data={(c.data as IContent.FeatureV2ComponentData[])[0]}
          />
        );
      case "grouped-component":
        return (
          <ComponentGroup
            key={c.name}
            data={(c.data as IContent.GroupedComponentData[])[0]}
          />
        );
      case "static":
        return <GetStaticComponent key={c.name} name={c.name} />;
      case "modal-component":
        return (
          <ModalComponent
            key={c.name}
            data={(c.data as IContent.ModalComponentData[])[0]}
          />
        );
      case "section-header-component":
        return (
          <SectionHeaderComponent
            key={c.name}
            badgeIcon={
              (c.data as IContent.SectionHeaderComponentData[])[0].badge_icon
            }
            badgeTitle={
              (c.data as IContent.SectionHeaderComponentData[])[0].badge_title
            }
            buttons={
              (c.data as IContent.SectionHeaderComponentData[])[0].buttons
            }
            description={
              (c.data as IContent.SectionHeaderComponentData[])[0].description
            }
            title={(c.data as IContent.SectionHeaderComponentData[])[0].title}
            badgeTitleTheme={
              (c.data as IContent.SectionHeaderComponentData[])[0]
                .badge_title_theme
            }
          />
        );
      case "media-component":
        return (
          <MediaComponent
            key={c.name}
            bgImage={(c.data as IContent.MediaComponentData[])[0].bg_image}
            file={(c.data as IContent.MediaComponentData[])[0].file}
            name={(c.data as IContent.MediaComponentData[])[0].name}
            wistiaId={
              (c.data as IContent.MediaComponentData[])[0].wistia_video_id
            }
            mediaComponentId={(c.data as IContent.MediaComponentData[])[0].id}
          />
        );
      case "divider-component":
        return (
          <DividerComponent
            key={c.name}
            color={(c.data as IContent.DividerComponentData[])[0].color}
          />
        );
      case "summary-feature-group-component":
        return (
          <SummaryFeatureGroupComponent
            key={c.name}
            features={
              (c.data as IContent.SummaryFeatureGroupComponentData[])[0]
                .features
            }
          />
        );
      case "button-group-component":
        return (
          <ButtonGroup
            key={c.name}
            title={(c.data as IContent.ButtonGroupData[])[0].title}
            isSticky={(c.data as IContent.ButtonGroupData[])[0].is_sticky}
            buttons={(c.data as IContent.ButtonGroupData[])[0].buttons}
          />
        );
      default:
        return <></>;
    }
  }) as any;
};

interface Props {
  components: IContent.IContentTypeResponse["components"];
  lang: any;
}
